<template>
  <figure
    class="dyn-cdn-picture"
    :class="{
      'auto-grow': autoGrow,
      'is-loading': isLoading,
      'is-letterboxed': isLetterboxed,
      'has-error': errorMessage,
    }"
  >
    <transition-group name="fade">
      <LoadingOverlay v-if="isLoading" key="loading-overlay" />
      <ErrorMessage v-else-if="errorMessage" key="error-message">
        <p>{{ errorMessage }}</p>
      </ErrorMessage>
    </transition-group>
    <template v-if="!errorMessage">
      <div
        ref="imageWrapper"
        class="image-wrapper"
        :class="{ 'auto-grow': autoGrow }"
        :style="imageWrapperStyle"
      >
        <img
          ref="imageElement"
          class="image-element"
          :class="{ 'auto-grow': autoGrow, 'has-known-size': hasKnownSize }"
          :height="height"
          :width="width"
          :alt="alt"
          :src="pictureUrl"
          @load="pictureLoaded"
          @error="loadError"
        />
      </div>
      <figcaption v-if="caption" class="caption">{{ caption }}</figcaption>
    </template>
  </figure>
</template>

<script>
import ErrorMessage from '../ui/ErrorMessage'
import LoadingOverlay from '../LoadingOverlay'
import { getDynCdnImageUrl } from '@/utils/network'

export default {
  name: 'DynCdnPicture',
  components: { ErrorMessage, LoadingOverlay },
  props: {
    pictureId: { type: [Number, String], required: true },
    alt: {
      type: String,
      required: false,
    },
    /**
     * Allows the element to grow vertically to fit the available space when used as a flex child.
     * Centers the picture vertically and crops the top/bottom to fit the available space.
     * If height/width is also given, the cropped height will never be smaller than what matches the ratio to the width.
     */
    autoGrow: {
      type: Boolean,
      default: false,
    },
    caption: {
      type: String,
      required: false,
      default: '',
    },
    height: {
      type: Number,
      required: false,
    },
    width: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      errorMessage: '',
      isLoading: true,
      /**
       * The size of the image wrapper element
       * @type {{width: number, height: number}}
       */
      canvasSize: { width: 0, height: 0 },

      /**
       * The rendered size of the image
       * @type {{width: number, height: number}}
       */
      renderedSize: { width: 0, height: 0 },
    }
  },
  computed: {
    pictureUrl() {
      return getDynCdnImageUrl(this.pictureId)
    },
    imageWrapperStyle() {
      const style = {}

      if (this.width) {
        style.maxWidth = `${this.width}px`
      }

      if (this.width && this.height) {
        style['--aspect-ratio-padding'] = (this.height / this.width) * 100 + '%'
      }

      return style
    },
    hasKnownSize() {
      return this.width && this.height
    },
    isLetterboxed() {
      return (
        this.renderedSize.height < this.canvasSize.height ||
        this.renderedSize.width < this.canvasSize.width
      )
    },
  },
  methods: {
    updateCachedSizes() {
      if (this.$refs.imageElement) {
        this.renderedSize = {
          height: this.$refs.imageElement.offsetHeight,
          width: this.$refs.imageElement.offsetWidth,
        }

        this.canvasSize = {
          height: this.$refs.imageWrapper.offsetHeight,
          width: this.$refs.imageWrapper.offsetWidth,
        }
      } else {
        this.renderedSize = {
          height: 0,
          width: 0,
        }
        this.canvasSize = {
          height: 0,
          width: 0,
        }
      }
    },
    pictureLoaded() {
      this.isLoading = false
      this.updateCachedSizes()
    },
    loadError() {
      this.isLoading = false
      this.errorMessage = this.$t('components.dyn-cdn-picture.load-error')
      this.updateCachedSizes()
    },
  },
  watch: {
    pictureId() {
      this.errorMessage = ''
    },
    autoGrow() {
      this.updateCachedSizes()
    },
  },
  mounted() {
    window.addEventListener('resize', this.updateCachedSizes)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateCachedSizes)
  },
}
</script>

<style lang="scss" scoped>
.dyn-cdn-picture {
  position: relative;
  display: block;

  &.auto-grow {
    flex-grow: 1;
    flex-basis: 0;
  }

  &.is-loading {
    .image-element,
    .caption {
      opacity: 0;
    }
  }
}

.image-wrapper {
  background: $grayLighter;

  &.auto-grow {
    height: 100%;
  }

  &:before {
    content: '';
    display: block;
    padding-top: var(--aspect-ratio-padding, 0);
  }
}

.image-element {
  display: block;
  margin: auto;
  max-width: 100%;
  height: auto;

  &.auto-grow {
    top: 50% !important;
    transform: translateY(-50%);
  }

  &.has-known-size {
    position: absolute;
    top: 0;
  }
}

.image-element,
.caption,
.fade-enter-active,
.fade-leave-active {
  transition: opacity $transitionMedium ease-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
